import { useEffect, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { DatePicker } from "react-advance-jalaali-datepicker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian.js";
import { useFetch } from "../../../custom/api";
import LoadingComp from "../../../components/LoadingComp";
import { notifyUnlocked } from "../../../components/achievements/AchievementUnlockToast";
import CalendarConflictModal from "./CalendarConflictModal";
import mealTypeLabels from "../mealTypeLabels";
import { formatJalali, todayIsoDate } from "../jalaliDate";
import "../../../styles/Modals.css";
import "../../../styles/Challenges.css";

function DatePickerInput(props) {
  return <input className="subject-1 grey-1 b-grey-5" {...props} />;
}

function formatUnixToIso(unix) {
  return new Date(unix * 1000).toISOString().substring(0, 10);
}

// The two-step explanation ("add to calendar" / "check in daily") already
// lives on the Challenge details page (spec section 7/9), so this sheet
// only needs to do the date-picking + preview + conflict-aware confirm -
// same request/toast/close shape as DietModal.js's transfer-to-calendar flow.
function ChallengeStartModal({ show, onClose, challenge, onStarted }) {
  const { myFetch } = useFetch();
  const [startDate, setStartDate] = useState(todayIsoDate());
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showConflict, setShowConflict] = useState(false);
  const [busy, setBusy] = useState(false);

  const todayFormatted = new DateObject({ date: new Date() })
    .setCalendar(persian)
    .format("YYYY-MM-DD");

  useEffect(() => {
    if (!show) return;
    let ignore = false;
    setLoadingPreview(true);
    const run = async () => {
      const resp = await myFetch(`challenges/${challenge.challenge_id}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_date: startDate }),
      });
      const r = await resp.json();
      if (!ignore) {
        setPreview(r);
        setLoadingPreview(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, startDate]);

  const changeStart = (unix) => setStartDate(formatUnixToIso(unix));

  const doStart = async (conflictResolution) => {
    setBusy(true);
    try {
      const resp = await myFetch(`challenges/${challenge.challenge_id}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_date: startDate, conflict_resolution: conflictResolution }),
      });
      const r = await resp.json();
      if (r.status === "success") {
        toast.success(r.message);
        setShowConflict(false);
        onClose();
        if (r.newly_unlocked && r.newly_unlocked.length > 0) {
          notifyUnlocked(r.newly_unlocked);
        }
        onStarted && onStarted();
      } else {
        toast.error(r.message || "این چالش الان قابل شروع نیست");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmClick = () => {
    if (!preview || preview.status !== "success") return;
    if (preview.conflicts && preview.conflicts.length > 0) {
      setShowConflict(true);
    } else {
      doStart("skip");
    }
  };

  if (!challenge) return null;

  return (
    <>
      <Modal isOpen={show} onRequestClose={onClose} className="modal" overlayClassName="overlay">
        <div className="modal-header">
          <h2>شروع چالش</h2>
          <a onClick={onClose}>
            <span className="icon-iconclose rectangle-1 heading-1 grey-3"></span>
          </a>
        </div>
        <div className="modal-body rightAlign">
          <p className="yekan grey-2 marginBottom1r">
            غذاهای این چالش بر اساس تاریخی که انتخاب می‌کنی به تقویمت اضافه می‌شن. می‌خوای
            چالش رو از چه تاریخی شروع کنی؟
          </p>
          <DatePicker
            inputComponent={DatePickerInput}
            placeholder="انتخاب تاریخ"
            format="jYYYY-jMM-jDD"
            onChange={changeStart}
            id="challengeStartDatePicker"
            preSelected={todayFormatted + "  " + "امروز"}
            containerClass={"grey-1"}
            customClass={"datepicker"}
          />

          <div className="marginTop5 max-height300" style={{ overflowY: "auto" }}>
            {loadingPreview ? (
              <LoadingComp />
            ) : preview && preview.status === "success" ? (
              preview.days.map((d) => (
                <div className="spc-between marginBottom1r" key={d.day_number}>
                  <span className="caption-1 grey-2 yekan">
                    روز {d.day_number} - {formatJalali(d.date)}
                  </span>
                  <span className="caption-1 grey-1 yekan">
                    {mealTypeLabels[d.meal_type] || d.meal_type}: {d.recipe_name}
                  </span>
                </div>
              ))
            ) : preview ? (
              <p className="caption-1 red-1 yekan">{preview.message}</p>
            ) : null}
          </div>
        </div>
        <div className="modal-footer">
          <a
            className="marginBottom0-4r fill-container radius1-5"
            style={{ opacity: busy || loadingPreview ? 0.6 : 1 }}
            onClick={() => !busy && !loadingPreview && handleConfirmClick()}
          >
            {busy ? "در حال افزودن..." : "افزودن به تقویم و شروع چالش"}
          </a>
        </div>
      </Modal>

      <CalendarConflictModal
        show={showConflict}
        onClose={() => setShowConflict(false)}
        conflicts={preview?.conflicts}
        onResolve={doStart}
        busy={busy}
      />
    </>
  );
}

export default ChallengeStartModal;
